'use client';

import { useCallback, useRef, useState, type Dispatch, type SetStateAction } from 'react';

import { createLogger } from '@core/be/platform/logging/Logger';

import type { Mindmap } from '@/features/mindmap/core/MindmapTypes';

type HistoryStack = {
  past: Mindmap[];
  future: Mindmap[];
};

interface UseMindmapHistoryOptions {
  initialMindmap: Mindmap;
  maxHistory?: number;
}

export interface SetDocWithHistoryOptions {
  historySnapshot?: Mindmap;
  skip?: boolean;
}

export type SetDocWithHistory = (
  updater: (prev: Mindmap) => Mindmap,
  options?: SetDocWithHistoryOptions
) => void;

interface UseMindmapHistoryResult {
  doc: Mindmap;
  setDoc: Dispatch<SetStateAction<Mindmap>>;
  setDocWithHistory: SetDocWithHistory;
  undo: () => void;
  redo: () => void;
  resetHistory: (next: Mindmap) => void;
}

export function useMindmapHistory({
  initialMindmap,
  maxHistory = 10,
}: UseMindmapHistoryOptions): UseMindmapHistoryResult {
  const log = useRef(createLogger('mindmap:history')).current;
  const [doc, setDoc] = useState<Mindmap>(initialMindmap);
  const historyRef = useRef<HistoryStack>({ past: [], future: [] });
  const undoRedoRef = useRef(false);

  const pushHistory = useCallback(
    (prev: Mindmap) => {
      const h = historyRef.current;
      h.past.push(prev);
      if (h.past.length > maxHistory) {
        h.past.shift();
      }
      h.future.length = 0;
    },
    [maxHistory]
  );

  const setDocWithHistory = useCallback(
    (updater: (prev: Mindmap) => Mindmap) => {
      setDoc((prev) => {
        const next = updater(prev);
        log.debug('setDocWithHistory %O', {
          prevEqNext: prev === next,
          nodesEq: prev.nodes === next.nodes,
          prevNodeCount: prev.nodes.length,
          nextNodeCount: next.nodes.length,
          prevFirstNode: prev.nodes[0],
          nextFirstNode: next.nodes[0],
        });
        if (!undoRedoRef.current && next !== prev) {
          log.debug('push history (next !== prev)');
          pushHistory(prev);
        } else {
          log.debug('skip push history %O', {
            undoRedo: undoRedoRef.current,
            nextEqPrev: next === prev,
          });
        }
        return next;
      });
    },
    [pushHistory]
  );

  const undo = useCallback(() => {
    const h = historyRef.current;
    if (!h.past.length) return;
    undoRedoRef.current = true;
    setDoc((current) => {
      const previous = h.past.pop()!;
      h.future.push(current);
      return previous;
    });
    undoRedoRef.current = false;
  }, []);

  const redo = useCallback(() => {
    const h = historyRef.current;
    if (!h.future.length) return;
    undoRedoRef.current = true;
    setDoc((current) => {
      const next = h.future.pop()!;
      h.past.push(current);
      return next;
    });
    undoRedoRef.current = false;
  }, []);

  const resetHistory = useCallback((nextDoc: Mindmap) => {
    historyRef.current = { past: [], future: [] };
    undoRedoRef.current = false;
    setDoc(nextDoc);
  }, []);

  return { doc, setDoc, setDocWithHistory, undo, redo, resetHistory };
}

export default useMindmapHistory;
